import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, Globe } from 'lucide-react';
import BrandLogo from './BrandLogo';
import { getReviewCulturePair, isReviewCulturePath } from '../pages/ReviewCulturePage';
import { isTapNWinPath, getTapNWinPath } from '../pages/TapNWinPage';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleLanguageToggle = () => {
    // If on a Review Culture page, navigate to the paired language page
    const rcPair = getReviewCulturePair(location.pathname);
    // If on a Tap'N Win page, navigate to the paired language page
    const newLang = language === 'en' ? 'fr' : 'en';
    toggleLanguage();
    if (rcPair) {
      navigate(rcPair);
    } else if (isTapNWinPath(location.pathname)) {
      navigate(getTapNWinPath(newLang));
    }
  };

  // Simplified top navigation - global-level items only
  const tnwPath = language === 'fr' ? '/fr/tapez-et-gagnez' : '/en/tap-n-win';
  const tnwLabel = language === 'fr' ? "Tapez et Gagnez" : "Tap'N Win";
  const rcPath = language === 'fr' ? '/fr/culture-des-avis' : '/review-culture';
  const rcLabel = language === 'fr' ? 'Culture des avis' : 'Review Culture';
  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/platform', label: t('nav.platform') },
    { path: '/insights', label: t('nav.insights') },
    { path: '/hardware', label: t('nav.hardware') },
    { path: tnwPath, label: tnwLabel },
    { path: '/implementation', label: t('nav.implementation') },
    { path: '/supporting-data', label: t('nav.supportingData') },
    { path: rcPath, label: rcLabel },
  ];

  const isActive = (path) => {
    if (path === rcPath) {
      return location.pathname.startsWith('/review-culture') || location.pathname.startsWith('/fr/culture-des-avis');
    }
    if (path === tnwPath) {
      return isTapNWinPath(location.pathname);
    }
    return location.pathname === path;
  };

  return (
    <header 
      data-testid="header"
      className="sticky top-0 z-50 bg-[#0D0D0D]/95 backdrop-blur-md border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28 lg:h-36">
          {/* Logo */}
          <div className="flex-shrink-0">
            <BrandLogo className="h-24 lg:h-32" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-0.5" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`whitespace-nowrap px-2 py-2 text-xs font-medium rounded-lg transition-colors ${
                  isActive(link.path)
                    ? 'text-white bg-white/10'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2 lg:gap-3">
            {/* Language Toggle */}
            <button
              onClick={handleLanguageToggle}
              data-testid="language-toggle"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white/70 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
            >
              <Globe className="w-4 h-4" strokeWidth={1.5} />
              <span className="uppercase">{language}</span>
            </button>

            {/* Marcom Button — distinct indigo product color */}
            <Link to="/marcom" data-testid="marco-nav-btn">
              <Button 
                variant="outline" 
                className="hidden sm:flex font-medium"
                style={{ color: '#6366f1', borderColor: 'rgba(99,102,241,0.3)', backgroundColor: 'rgba(99,102,241,0.1)' }}
              >
                Marcom
              </Button>
            </Link>

            {/* Portal Button — Green */}
            <a href="https://portal.yulontap.com" data-testid="portal-btn"
              className="hidden sm:inline-flex items-center justify-center h-9 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors hover:opacity-80"
              style={{ color: '#22c55e', border: '1px solid rgba(34,197,94,0.5)', backgroundColor: 'rgba(34,197,94,0.15)' }}
              target="_blank" rel="noopener noreferrer"
            >
              {t('nav.portal')}
            </a>

            {/* Demo CTA - Premium orange gradient */}
            <Link to="/contact" data-testid="demo-btn">
              <Button className="hidden sm:flex btn-demo-cta text-white border-0">
                {t('nav.requestDemo')}
              </Button>
            </Link>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="xl:hidden">
                <Button variant="ghost" size="icon" className="text-white" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-[#0D0D0D] border-white/10">
                <div className="flex flex-col gap-6 mt-6">
                  <nav className="flex flex-col gap-1" data-testid="mobile-nav">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                        className={`px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                          isActive(link.path)
                            ? 'text-white bg-white/10'
                            : 'text-white/70 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </nav>
                  <div className="flex flex-col gap-2 px-4">
                    <Link to="/marcom" onClick={() => setIsOpen(false)}>
                      <Button 
                        variant="outline" 
                        className="w-full font-medium"
                        style={{ color: '#6366f1', borderColor: 'rgba(99,102,241,0.3)', backgroundColor: 'rgba(99,102,241,0.1)' }}
                        data-testid="mobile-marco-btn"
                      >
                        Marcom
                      </Button>
                    </Link>
                    <a href="https://portal.yulontap.com" onClick={() => setIsOpen(false)}
                      className="w-full flex items-center justify-center h-10 rounded-md text-sm font-medium hover:opacity-80"
                      style={{ color: '#22c55e', border: '1px solid rgba(34,197,94,0.5)', backgroundColor: 'rgba(34,197,94,0.15)' }}
                      data-testid="mobile-portal-btn"
                      target="_blank" rel="noopener noreferrer"
                    >
                      {t('nav.portal')}
                    </a>
                    <Link to="/contact" onClick={() => setIsOpen(false)}>
                      <Button 
                        className="w-full btn-demo-cta text-white"
                        data-testid="mobile-demo-btn"
                      >
                        {t('nav.requestDemo')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
